import React from 'react';
import { safeGet } from '../../utils/trainingUtils';
import { TrainingIcon } from '../TrainingIcon';

/**
 * Human Rights Training Coverage Details — compares the number of employees and
 * workers (permanent / other-than-permanent) covered by Human Rights training
 * against their totals, for the current and previous financial years.
 */
export const HumanRightsTrainingCoverageDetails = ({
    trainingData,
    previousTrainingData,
    traineeList,
    previousTraineeList,
    currentFYLabel,
    previousFYLabel,
    categoryCoverage,
    previousCategoryCoverage,
    categoryIds
}) => {
    /**
     * Counts registered trainees per category (employees/workers) and employment
     * type (permanent / other-than-permanent) from a trainee list.
     */
    const processTraineeList = (list) => {
        const structure = {
            employees: {
                permanent: { total: 0 },
                otherThanPermanent: { total: 0 }
            },
            workers: {
                permanent: { total: 0 },
                otherThanPermanent: { total: 0 }
            }
        };

        if (!list || !Array.isArray(list)) return structure;

        list.forEach((trainee) => {
            if (!trainee) return;

            const categoryValue = trainee.categoryId || trainee.category_name ||
                                 trainee.employeeType || trainee.category || trainee.type;

            let targetCategory = null;
            let traineeEmploymentType = null;

            if (categoryValue) {
                const categoryStr = categoryValue.toString().toLowerCase();

                if (categoryValue === 'Permanent Employee') {
                    targetCategory = 'employees';
                    traineeEmploymentType = 'permanent';
                } else if (categoryValue === "Other than Permanent Employee") {
                    targetCategory = 'employees';
                    traineeEmploymentType = 'otherThanPermanent';
                } else if (categoryStr === 'kmp' || categoryStr.includes('kmp')) {
                    targetCategory = 'employees';
                    traineeEmploymentType = 'permanent';
                } else if (categoryValue === 'Permanent Worker') {
                    targetCategory = 'workers';
                    traineeEmploymentType = 'permanent';
                } else if (categoryValue === 'Other than Permanent Worker') {
                    targetCategory = 'workers';
                    traineeEmploymentType = 'otherThanPermanent';
                }
            }

            if (targetCategory && traineeEmploymentType) {
                structure[targetCategory][traineeEmploymentType].total++;
            }
        });

        return structure;
    };

    /**
     * Collects the unique employee ids covered by Human Rights training (category
     * id = 3) per category and employment type.
     */
    const processTrainingData = (data) => {
        const covered = {
            employees: {
                permanent: new Set(),
                otherThanPermanent: new Set()
            },
            workers: {
                permanent: new Set(),
                otherThanPermanent: new Set()
            }
        };

        if (!data || !Array.isArray(data)) return covered;

        data.forEach((training) => {
            if (!training) return;

            const trainingCategoryIds = (training.categories || [])
                .map(category => category?.id)
                .filter(Boolean);

            const humanRightsId = categoryIds?.humanRights;
            if (humanRightsId == null || !trainingCategoryIds.includes(humanRightsId)) {
                return;
            }

            const attendants = training.attendantUsers || [];

            attendants.forEach((attendant) => {
                if (!attendant) return;

                const categoryValue = attendant.categoryId || attendant.category_name ||
                                     attendant.employeeType || attendant.category || attendant.type;
                const employeeId = attendant.id;

                if (!employeeId || !categoryValue) return;

                let targetCategory = null;
                let traineeEmploymentType = null;
                const categoryStr = categoryValue.toString().toLowerCase();

                if (categoryValue === 'Permanent Employee') {
                    targetCategory = 'employees';
                    traineeEmploymentType = 'permanent';
                } else if (categoryValue === "Other than Permanent Employee") {
                    targetCategory = 'employees';
                    traineeEmploymentType = 'otherThanPermanent';
                } else if (categoryStr === 'kmp' || categoryStr.includes('kmp')) {
                    targetCategory = 'employees';
                    traineeEmploymentType = 'permanent';
                } else if (categoryValue === 'Permanent Worker') {
                    targetCategory = 'workers';
                    traineeEmploymentType = 'permanent';
                } else if (categoryValue === 'Other than Permanent Worker') {
                    targetCategory = 'workers';
                    traineeEmploymentType = 'otherThanPermanent';
                }

                if (targetCategory && traineeEmploymentType) {
                    covered[targetCategory][traineeEmploymentType].add(employeeId);
                }
            });
        });

        return covered;
    };

    /**
     * Current-FY per-employment-type totals from the optimized-analytics
     * categoryCoverage. Employee totals are the permanent and other-than-permanent
     * employee headcounts — permanentEmployee already includes KMP, so KMP folds into
     * the Permanent row to match the covered numerator; BoD is excluded. Falls back to
     * the trainee count when unavailable.
     */
    const totalsFromCoverage = (coverage) => {
        const total = (key) => coverage?.[key]?.headcount?.total ?? 0;
        return {
            employees: {
                permanent: { total: total('permanentEmployee') },
                otherThanPermanent: { total: total('otherThanPermanentEmployee') }
            },
            workers: {
                permanent: { total: total('permanentWorker') },
                otherThanPermanent: { total: total('otherThanPermanentWorker') }
            }
        };
    };

    const currentTotals = categoryCoverage
        ? totalsFromCoverage(categoryCoverage)
        : processTraineeList(traineeList);
    const currentCovered = processTrainingData(trainingData);

    const previousTotals = previousCategoryCoverage
        ? totalsFromCoverage(previousCategoryCoverage)
        : processTraineeList(previousTraineeList);
    const previousCovered = processTrainingData(previousTrainingData);

    const analysis = {
        currentFY: {
            employees: {
                permanent: {
                    total: currentTotals.employees.permanent.total,
                    coveredCount: currentCovered.employees.permanent.size
                },
                otherThanPermanent: {
                    total: currentTotals.employees.otherThanPermanent.total,
                    coveredCount: currentCovered.employees.otherThanPermanent.size
                }
            },
            workers: {
                permanent: {
                    total: currentTotals.workers.permanent.total,
                    coveredCount: currentCovered.workers.permanent.size
                },
                otherThanPermanent: {
                    total: currentTotals.workers.otherThanPermanent.total,
                    coveredCount: currentCovered.workers.otherThanPermanent.size
                }
            }
        },
        previousFY: {
            employees: {
                permanent: {
                    total: previousTotals.employees.permanent.total,
                    coveredCount: previousCovered.employees.permanent.size
                },
                otherThanPermanent: {
                    total: previousTotals.employees.otherThanPermanent.total,
                    coveredCount: previousCovered.employees.otherThanPermanent.size
                }
            },
            workers: {
                permanent: {
                    total: previousTotals.workers.permanent.total,
                    coveredCount: previousCovered.workers.permanent.size
                },
                otherThanPermanent: {
                    total: previousTotals.workers.otherThanPermanent.total,
                    coveredCount: previousCovered.workers.otherThanPermanent.size
                }
            }
        }
    };

    /**
     * Formats a "total | covered | %" display string and a data-driven colour
     * (green when anyone is covered, red otherwise).
     */
    const formatDisplayValue = (total, covered) => {
        const percentage = total > 0 ? ((covered / total) * 100).toFixed(2) : '0';
        return {
            display: `${total} | ${covered} | ${percentage}%`,
            color: covered > 0 ? '#059669' : '#DC2626'
        };
    };

    return (
        <div className="trd-section">
            <div className="trd-section-head">
                <span className="trd-section-icon"><TrainingIcon name="heart" size={18} /></span>
                <div>
                    <h2 className="trd-section-title">Human Rights Training Coverage Details</h2>
                </div>
            </div>

            <div className="trd-block">
                <h3 className="trd-block-title"><TrainingIcon name="users" size={15} />Employees including KMPs</h3>

                <div className="trd-table-wrap">
                    <table className="trd-table">
                        <thead>
                            <tr>
                                <th className="trd-th-label">Category</th>
                                <th>
                                    <div>FY {currentFYLabel}</div>
                                    <span className="trd-th-sub">Total | Covered | %</span>
                                </th>
                                <th>
                                    <div>FY {previousFYLabel}</div>
                                    <span className="trd-th-sub">Total | Covered | %</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="trd-tr">
                                <td className="trd-td-label">Permanent</td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'currentFY.employees.permanent.total'),
                                            safeGet(analysis, 'currentFY.employees.permanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'previousFY.employees.permanent.total'),
                                            safeGet(analysis, 'previousFY.employees.permanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                            </tr>
                            <tr className="trd-tr">
                                <td className="trd-td-label">Other than Permanent</td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'currentFY.employees.otherThanPermanent.total'),
                                            safeGet(analysis, 'currentFY.employees.otherThanPermanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'previousFY.employees.otherThanPermanent.total'),
                                            safeGet(analysis, 'previousFY.employees.otherThanPermanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                            </tr>
                            <tr className="trd-tr-total">
                                <td className="trd-td-label">Total Employees</td>
                                <td>
                                    {(() => {
                                        const totalEmployees = safeGet(analysis, 'currentFY.employees.permanent.total') +
                                            safeGet(analysis, 'currentFY.employees.otherThanPermanent.total');
                                        const totalCovered = safeGet(analysis, 'currentFY.employees.permanent.coveredCount') +
                                            safeGet(analysis, 'currentFY.employees.otherThanPermanent.coveredCount');
                                        const result = formatDisplayValue(totalEmployees, totalCovered);
                                        return <span style={{ color: result.color, fontWeight: "600" }}>{result.display}</span>;
                                    })()}
                                </td>
                                <td>
                                    {(() => {
                                        const totalEmployees = safeGet(analysis, 'previousFY.employees.permanent.total') +
                                            safeGet(analysis, 'previousFY.employees.otherThanPermanent.total');
                                        const totalCovered = safeGet(analysis, 'previousFY.employees.permanent.coveredCount') +
                                            safeGet(analysis, 'previousFY.employees.otherThanPermanent.coveredCount');
                                        const result = formatDisplayValue(totalEmployees, totalCovered);
                                        return <span style={{ color: result.color, fontWeight: "600" }}>{result.display}</span>;
                                    })()}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="trd-block">
                <h3 className="trd-block-title"><TrainingIcon name="briefcase" size={15} />Workers</h3>

                <div className="trd-table-wrap">
                    <table className="trd-table">
                        <thead>
                            <tr>
                                <th className="trd-th-label">Category</th>
                                <th>
                                    <div>FY {currentFYLabel}</div>
                                    <span className="trd-th-sub">Total | Covered | %</span>
                                </th>
                                <th>
                                    <div>FY {previousFYLabel}</div>
                                    <span className="trd-th-sub">Total | Covered | %</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="trd-tr">
                                <td className="trd-td-label">Permanent</td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'currentFY.workers.permanent.total'),
                                            safeGet(analysis, 'currentFY.workers.permanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'previousFY.workers.permanent.total'),
                                            safeGet(analysis, 'previousFY.workers.permanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                            </tr>
                            <tr className="trd-tr">
                                <td className="trd-td-label">Other than Permanent</td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'currentFY.workers.otherThanPermanent.total'),
                                            safeGet(analysis, 'currentFY.workers.otherThanPermanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                                <td>
                                    {(() => {
                                        const result = formatDisplayValue(
                                            safeGet(analysis, 'previousFY.workers.otherThanPermanent.total'),
                                            safeGet(analysis, 'previousFY.workers.otherThanPermanent.coveredCount')
                                        );
                                        return <span style={{ color: result.color, fontWeight: "500" }}>{result.display}</span>;
                                    })()}
                                </td>
                            </tr>
                            <tr className="trd-tr-total">
                                <td className="trd-td-label">Total Workers</td>
                                <td>
                                    {(() => {
                                        const totalWorkers = safeGet(analysis, 'currentFY.workers.permanent.total') +
                                            safeGet(analysis, 'currentFY.workers.otherThanPermanent.total');
                                        const totalCovered = safeGet(analysis, 'currentFY.workers.permanent.coveredCount') +
                                            safeGet(analysis, 'currentFY.workers.otherThanPermanent.coveredCount');
                                        const result = formatDisplayValue(totalWorkers, totalCovered);
                                        return <span style={{ color: result.color, fontWeight: "600" }}>{result.display}</span>;
                                    })()}
                                </td>
                                <td>
                                    {(() => {
                                        const totalWorkers = safeGet(analysis, 'previousFY.workers.permanent.total') +
                                            safeGet(analysis, 'previousFY.workers.otherThanPermanent.total');
                                        const totalCovered = safeGet(analysis, 'previousFY.workers.permanent.coveredCount') +
                                            safeGet(analysis, 'previousFY.workers.otherThanPermanent.coveredCount');
                                        const result = formatDisplayValue(totalWorkers, totalCovered);
                                        return <span style={{ color: result.color, fontWeight: "600" }}>{result.display}</span>;
                                    })()}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
